import { RiInformationLine } from "@itandi/itandi-bb-ui";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useEnabledStaffs } from "~/hooks/useStaffs";
import type { Deal } from "~/models/Deal";
import { matchDealType } from "~/models/Deal";
import type { DealUpdateParam } from "~/models/DealForm";
import type { Staff } from "~/models/Staff";
import { toDate } from "~/utils/DateUtil";
import { onChangeDate } from "~/utils/FormUtil";
import Styles from "./Block.module.scss";

type Props = {
	deal: Deal;
};

export const DealBlock: React.FC<Props> = ({ deal }: Props) => {
	const staffs = useEnabledStaffs().data;
	const { register, watch, setValue, formState } =
		useFormContext<DealUpdateParam>();
	const { errors } = formState;
	const onChangeStaff = React.useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>): void => {
			const { value } = e.target;
			if (staffs !== null) {
				const selectStaff =
					staffs.find((s: Staff) => s.id === Number(value)) ?? null;
				setValue("staff", selectStaff);
			}
		},
		[staffs, setValue]
	);
	const staff = watch("staff");
	const terminationDate = watch("terminationDate");
	if (deal === null) return null;
	return (
		<div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormRowLabel}>
					<RiInformationLine className={Styles.BlockIcon} />
					その他情報
				</div>
			</div>
			{matchDealType(deal, "restoration") && (
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						<span className={Styles.FormLabelText}>解約日</span>
					</div>
					<div className={Styles.FormSettingCol}>
						<div>
							<InputDatev2
								maxWidth="13rem"
								onChange={(date): void => {
									onChangeDate(date, "terminationDate", setValue);
								}}
								value={toDate(terminationDate)}
							/>
						</div>
					</div>
				</div>
			)}
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>案件担当者</span>
					<PresentationChip variant="red">必須</PresentationChip>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<InputSelect
							className={Styles.InputSelect}
							onChange={onChangeStaff}
							value={String(staff?.id ?? "")}
						>
							{staffs !== null &&
								staffs.map((s: Staff) => (
									<option key={s.id} value={s.id}>
										{s.name}
									</option>
								))}
						</InputSelect>
					</div>
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>発注書請書の宛名</span>
					<PresentationChip variant="red">必須</PresentationChip>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<Input
							isInvalid={!!errors.estimateAddressName}
							placeholder="例）サンプル株式会社"
							{...register("estimateAddressName")}
						/>
					</div>
					{errors.estimateAddressName ? (
						<div className={Styles.InvalidFeedback}>
							{errors.estimateAddressName.message}
						</div>
					) : null}
					<p className={Styles.Description}>
						発注者が貸主（オーナー）様になる場合は、貸主名を入力してください
						<br />
						修繕おまかせサービスの場合には、発注者は原則管理会社となります。
					</p>
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>その他ご要望、特筆事項</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<InputText rows={3} {...register("note")} />
					</div>
				</div>
			</div>
		</div>
	);
};
